import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import OccupancyBar from "@/components/OccupancyBar";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import {
  Network, Plus, Pencil, Filter, Search, MoreVertical, History, Building2,
  CheckCircle2, AlertTriangle, XCircle, AlertOctagon, TrendingDown, LayoutGrid, Table as TableIcon,
  Trash2, RefreshCw,
} from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const TONE = {
  completo:  { label: "Completo",  bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-200", color: "#22C55E", icon: CheckCircle2 },
  atencao:   { label: "Atenção",   bg: "bg-amber-100",   text: "text-amber-800",   ring: "ring-amber-200",   color: "#EAB308", icon: AlertTriangle },
  deficit:   { label: "Déficit",   bg: "bg-red-100",     text: "text-red-800",     ring: "ring-red-200",     color: "#EF4444", icon: XCircle },
  excedido:  { label: "Excedido",  bg: "bg-violet-100",  text: "text-violet-800",  ring: "ring-violet-200",  color: "#8B5CF6", icon: AlertOctagon },
  neutral:   { label: "Sem quadro", bg: "bg-slate-100",  text: "text-slate-700",   ring: "ring-slate-200",   color: "#8F9D97", icon: Network },
};

const emptySector = {
  company_id: "", name: "", code: "", unidade: "", responsavel: "",
  quadro_aprovado: 0, centro_custo: "", parent_sector_id: "",
  observacoes: "", status: "ativo", motivo_alteracao: "",
};

export default function SectorsPage() {
  const { companies, companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin");
  const [sectors, setSectors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all"); // sector id
  const [responsavelFilter, setResponsavelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [faixaFilter, setFaixaFilter] = useState("all"); // deficit|atencao|completo|excedido
  const [unidadeFilter, setUnidadeFilter] = useState("all");

  // CRUD
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySector);

  // History
  const [histOpen, setHistOpen] = useState(false);
  const [histSector, setHistSector] = useState(null);
  const [histItems, setHistItems] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = companyParam ? { company_id: companyParam } : {};
      const [s, sm, dash] = await Promise.all([
        api.get("/sectors", { params }),
        api.get("/sectors/summary", { params }),
        api.get("/sectors/dashboard", { params }),
      ]);
      setSectors(s.data);
      setSummary(sm.data);
      setDashboard(dash.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [companyParam]);

  const unidades = useMemo(() => {
    const set = new Set(sectors.map((s) => s.unidade).filter(Boolean));
    return Array.from(set);
  }, [sectors]);

  const responsaveis = useMemo(() => {
    const set = new Set(sectors.map((s) => s.responsavel).filter(Boolean));
    return Array.from(set);
  }, [sectors]);

  const filtered = useMemo(() => {
    return sectors.filter((s) => {
      if (sectorFilter !== "all" && s.id !== sectorFilter) return false;
      if (responsavelFilter !== "all" && s.responsavel !== responsavelFilter) return false;
      if (unidadeFilter !== "all" && s.unidade !== unidadeFilter) return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (faixaFilter !== "all" && s.tone !== faixaFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const co = companies.find((c) => c.id === s.company_id)?.nome_fantasia?.toLowerCase() || "";
        const hay = `${s.name} ${s.code} ${s.responsavel || ""} ${co} ${s.unidade || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sectors, search, sectorFilter, responsavelFilter, unidadeFilter, statusFilter, faixaFilter, companies]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptySector, company_id: companyParam || companies[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...emptySector, ...s, motivo_alteracao: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        quadro_aprovado: Number(form.quadro_aprovado || 0),
        parent_sector_id: form.parent_sector_id || null,
      };
      if (editing) {
        const changed = Number(editing.quadro_aprovado || 0) !== payload.quadro_aprovado;
        if (changed && !payload.motivo_alteracao?.trim()) {
          toast.error("Informe o motivo da alteração do quadro aprovado");
          return;
        }
        await api.put(`/sectors/${editing.id}`, payload);
        toast.success("Setor atualizado");
      } else {
        await api.post("/sectors", payload);
        toast.success("Setor criado");
      }
      setOpen(false);
      loadAll();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const openHistory = async (s) => {
    setHistSector(s);
    setHistOpen(true);
    setHistItems([]);
    try {
      const { data } = await api.get(`/sectors/${s.id}/history`);
      setHistItems(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const askDeleteSector = async (s) => {
    // First call without force to get the linked counts
    try {
      await api.delete(`/sectors/${s.id}`);
      toast.success(`Setor "${s.name}" inativado.`);
      loadAll();
    } catch (ex) {
      const status = ex.response?.status;
      const detail = ex.response?.data?.detail;
      if (status === 409 && detail?.links) {
        const links = detail.links;
        const summary = `Vínculos ativos detectados:\n` +
          `• ${links.employees} colaborador(es) ativo(s)\n` +
          `• ${links.child_sectors} sub-setor(es)\n` +
          `• ${links.work_boards} quadro(s) de trabalho\n\n` +
          `Confirma inativar mesmo assim? Os vínculos permanecem no banco e voltam ao reativar.`;
        if (!window.confirm(summary)) return;
        try {
          await api.delete(`/sectors/${s.id}`, { params: { force: true } });
          toast.success(`Setor "${s.name}" inativado com vínculos preservados.`);
          loadAll();
        } catch (ex2) {
          toast.error(formatApiError(ex2.response?.data?.detail));
        }
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const restoreSector = async (s) => {
    if (!window.confirm(`Reativar o setor "${s.name}"?`)) return;
    try {
      await api.post(`/sectors/${s.id}/restore`);
      toast.success("Setor reativado");
      loadAll();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const hardDeleteSector = async (s) => {
    if (!window.confirm(
      `Atenção: esta ação excluirá DEFINITIVAMENTE o setor "${s.name}".\n\n` +
      `Caso existam vínculos (colaboradores, sub-setores ou quadros), a exclusão não será permitida.\n\nDeseja continuar?`
    )) return;
    const confirm2 = window.prompt("Para confirmar a exclusão PERMANENTE, digite EXCLUIR:");
    if (confirm2 !== "EXCLUIR") { toast.info("Exclusão cancelada."); return; }
    try {
      await api.delete(`/sectors/${s.id}/hard`);
      toast.success("Setor excluído definitivamente.");
      loadAll();
    } catch (ex) {
      const status = ex.response?.status;
      const detail = ex.response?.data?.detail;
      if (status === 409) {
        const links = detail?.links || {};
        toast.error(`${detail?.message || "Bloqueado"} · ${links.employees || 0} colab., ${links.child_sectors || 0} sub-setores, ${links.work_boards || 0} quadros`);
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="sectors-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Estrutura organizacional</div>
          <h1 className="font-display text-3xl font-semibold">Setores &amp; Quadro aprovado</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Gestão real do quadro de pessoal com semáforo, histórico e alertas.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="sector-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo setor
          </Button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile loading={loading} label="Total de setores" value={summary?.total_setores} icon={Network} tone="primary" />
        <StatTile loading={loading} label="Completos" value={summary?.completos} icon={CheckCircle2} tone="success" />
        <StatTile loading={loading} label="Em atenção" value={summary?.atencao} icon={AlertTriangle} tone="warning" />
        <StatTile loading={loading} label="Em déficit" value={summary?.deficit} icon={XCircle} tone="danger" />
        <StatTile loading={loading} label="Excedidos" value={summary?.excedidos} icon={AlertOctagon} tone="violet"
          hint={`Ocupação média: ${summary?.ocupacao_media ?? 0}%`} />
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por setor, empresa, unidade ou responsável..."
            className="pl-9"
            data-testid="sector-search-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <FilterSelect value={unidadeFilter} onChange={setUnidadeFilter} placeholder="Unidade"
            items={[{ v: "all", l: "Todas unidades" }, ...unidades.map((u) => ({ v: u, l: u }))]} testId="filter-unidade" />
          <FilterSelect value={sectorFilter} onChange={setSectorFilter} placeholder="Setor"
            items={[{ v: "all", l: "Todos setores" }, ...sectors.map((s) => ({ v: s.id, l: s.name }))]} testId="filter-setor" />
          <FilterSelect value={responsavelFilter} onChange={setResponsavelFilter} placeholder="Responsável"
            items={[{ v: "all", l: "Todos responsáveis" }, ...responsaveis.map((r) => ({ v: r, l: r }))]} testId="filter-resp" />
          <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="Status"
            items={[{ v: "all", l: "Todos status" }, { v: "ativo", l: "Ativo" }, { v: "inativo", l: "Inativo" }]} testId="filter-status" />
          <FilterSelect value={faixaFilter} onChange={setFaixaFilter} placeholder="Ocupação"
            items={[
              { v: "all", l: "Toda ocupação" },
              { v: "completo", l: "Completo" },
              { v: "atencao", l: "Atenção (80-99%)" },
              { v: "deficit", l: "Déficit (<80%)" },
              { v: "excedido", l: "Excedido" },
            ]} testId="filter-faixa" />
        </div>
      </div>

      {/* Tabs: Table | Dashboard */}
      <Tabs defaultValue="tabela" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="tabela" data-testid="tab-tabela"><TableIcon className="h-3.5 w-3.5 mr-1.5" /> Tabela</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutGrid className="h-3.5 w-3.5 mr-1.5" /> Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="tabela" className="m-0">
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead className="text-right">Aprovado</TableHead>
                  <TableHead className="text-right">Atual</TableHead>
                  <TableHead className="text-right">Diferença</TableHead>
                  <TableHead className="w-[200px]">Ocupação</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-5" /></TableCell></TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">Nenhum setor encontrado.</TableCell></TableRow>
                ) : filtered.map((s) => {
                  const co = companies.find((c) => c.id === s.company_id);
                  const tone = TONE[s.tone] || TONE.neutral;
                  const TIcon = tone.icon;
                  const diff = s.diferenca ?? ((s.quadro_atual || 0) - (s.quadro_aprovado || 0));
                  const diffColor = diff > 0 ? "text-violet-700" : diff === 0 ? "text-emerald-700" : "text-amber-700";
                  return (
                    <TableRow key={s.id} className="rh-row" data-testid={`sector-row-${s.id}`}>
                      <TableCell className="text-[12.5px]">{co?.nome_fantasia || "—"}</TableCell>
                      <TableCell className="text-[12.5px]">{s.unidade || "—"}</TableCell>
                      <TableCell className="font-medium">
                        <div>{s.name}</div>
                        <div className="text-[11px] text-muted-foreground">{s.code || "—"}</div>
                      </TableCell>
                      <TableCell className="text-[12.5px]">{s.responsavel || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{s.quadro_aprovado || 0}</TableCell>
                      <TableCell className="text-right font-mono">{s.quadro_atual || 0}</TableCell>
                      <TableCell className={`text-right font-mono font-medium ${diffColor}`}>
                        {diff > 0 ? "+" : ""}{diff}
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        <OccupancyBar current={s.quadro_atual || 0} approved={s.quadro_aprovado || 0} size="sm" />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`ring-1 ring-inset ${tone.bg} ${tone.text} ${tone.ring}`}>
                          <TIcon className="h-3 w-3 mr-1" /> {tone.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`sector-actions-${s.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && (
                              <DropdownMenuItem onSelect={() => openEdit(s)} data-testid={`sector-edit-${s.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Editar setor
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onSelect={() => { window.location.href = `/setores/${s.id}/quadro`; }}
                              data-testid={`sector-auto-grid-${s.id}`}
                            >
                              <LayoutGrid className="h-3.5 w-3.5 mr-2 text-[#C5A059]" /> <b>Quadro automático</b>
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => openHistory(s)} data-testid={`sector-history-${s.id}`}>
                              <History className="h-3.5 w-3.5 mr-2" /> Histórico de quadro
                            </DropdownMenuItem>
                            {canEdit && s.status !== "inativo" && (
                              <DropdownMenuItem onSelect={() => askDeleteSector(s)}
                                                className="text-rose-700"
                                                data-testid={`sector-delete-${s.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Inativar setor
                              </DropdownMenuItem>
                            )}
                            {hasRole("master") && (
                              <DropdownMenuItem onSelect={() => hardDeleteSector(s)}
                                                className="text-rose-800 font-semibold"
                                                data-testid={`sector-hard-delete-${s.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-2" /> Excluir setor (definitivo)
                              </DropdownMenuItem>
                            )}
                            {canEdit && s.status === "inativo" && (
                              <DropdownMenuItem onSelect={() => restoreSector(s)}
                                                className="text-emerald-700"
                                                data-testid={`sector-restore-${s.id}`}>
                                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Reativar setor
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="border-t border-border px-4 py-2 text-[12px] text-muted-foreground">
              {filtered.length} de {sectors.length} setores exibidos
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dashboard" className="m-0 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-lg border border-border bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Ocupação</div>
                  <h3 className="font-display text-lg font-semibold mt-1">Percentual por setor</h3>
                </div>
                <Building2 className="h-4 w-4 text-[#C5A059]" />
              </div>
              <div className="h-[340px] mt-4">
                {loading ? <Skeleton className="h-full w-full" /> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboard?.occupation_by_sector || []} margin={{ top: 8, right: 8, left: -10, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F4" />
                      <XAxis dataKey="sector" stroke="#8F9D97" fontSize={11} angle={-20} textAnchor="end" height={70} />
                      <YAxis stroke="#8F9D97" fontSize={12} unit="%" />
                      <Tooltip contentStyle={{ border: "1px solid #E2E8F4", borderRadius: 8 }} />
                      <Bar dataKey="pct" name="% Ocupação" radius={[4, 4, 0, 0]}>
                        {(dashboard?.occupation_by_sector || []).map((e) => (
                          <Cell key={e.sector} fill={(TONE[e.tone] || TONE.neutral).color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Rankings</div>
                  <h3 className="font-display text-lg font-semibold mt-1">Top déficits</h3>
                </div>
                <TrendingDown className="h-4 w-4 text-red-500" />
              </div>
              <div className="mt-3 space-y-3 max-h-[340px] overflow-y-auto pr-1">
                {(dashboard?.top_deficit || []).map((s) => (
                  <div key={s.sector} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-medium text-[13px] truncate">{s.sector}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{s.company}</div>
                      </div>
                      <span className="text-[11px] text-muted-foreground tabular-nums">{s.current}/{s.approved}</span>
                    </div>
                    <div className="mt-2">
                      <OccupancyBar current={s.current} approved={s.approved} size="sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Comparativo</div>
                <h3 className="font-display text-lg font-semibold mt-1">Empresas do grupo · quadro aprovado x atual</h3>
              </div>
              <Building2 className="h-4 w-4 text-[#C5A059]" />
            </div>
            <div className="h-[300px] mt-4">
              {loading ? <Skeleton className="h-full w-full" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard?.by_company || []} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F4" />
                    <XAxis dataKey="company" stroke="#8F9D97" fontSize={12} />
                    <YAxis stroke="#8F9D97" fontSize={12} />
                    <Tooltip contentStyle={{ border: "1px solid #E2E8F4", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="approved" name="Aprovado" fill="#C5A059" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="current" name="Atual" fill="#234935" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar setor" : "Novo setor"}</DialogTitle>
            <DialogDescription>
              Cadastre dados da estrutura organizacional, quadro aprovado e responsabilidade.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Identificação</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="form-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Unidade / filial" value={form.unidade} set={(v) => setForm({ ...form, unidade: v })} testId="form-unidade"
               placeholder="Ex: Matriz, Filial Norte..." />
            <F label="Nome do setor" value={form.name} set={(v) => setForm({ ...form, name: v })} testId="form-name" />
            <F label="Código do setor" value={form.code} set={(v) => setForm({ ...form, code: v })} testId="form-code" />
            <F label="Responsável" value={form.responsavel} set={(v) => setForm({ ...form, responsavel: v })} testId="form-resp" />
            <F label="Centro de custo" value={form.centro_custo} set={(v) => setForm({ ...form, centro_custo: v })} testId="form-cc" />

            <div className="md:col-span-2 mt-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Hierarquia</div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Setor pai (opcional)</Label>
              <Select
                value={form.parent_sector_id || "none"}
                onValueChange={(v) => setForm({ ...form, parent_sector_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="form-parent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhum (setor raiz) —</SelectItem>
                  {sectors
                    .filter((s) => s.company_id === form.company_id && s.id !== editing?.id)
                    .map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 mt-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Quadro e status</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Quadro aprovado</Label>
              <Input type="number" min="0" value={form.quadro_aprovado}
                     onChange={(e) => setForm({ ...form, quadro_aprovado: e.target.value })}
                     data-testid="form-quadro" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editing && Number(editing.quadro_aprovado || 0) !== Number(form.quadro_aprovado || 0) && (
              <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-medium mb-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Motivo da alteração do quadro (obrigatório)
                </div>
                <Input
                  value={form.motivo_alteracao}
                  onChange={(e) => setForm({ ...form, motivo_alteracao: e.target.value })}
                  placeholder="Justifique o ajuste do quadro aprovado (será registrado no histórico)"
                  data-testid="form-motivo"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
              <Input value={form.observacoes || ""}
                     onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} data-testid="sector-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History className="h-4 w-4" /> Histórico de quadro · {histSector?.name}
            </DialogTitle>
            <DialogDescription>
              Alterações do quadro aprovado, com responsável e motivo registrado.
            </DialogDescription>
          </DialogHeader>
          {histItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-[13px]">Nenhuma alteração registrada até o momento.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>De</TableHead>
                  <TableHead>Para</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Autor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {histItems.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono text-[12px]">{fmtDateTime(h.at)}</TableCell>
                    <TableCell className="font-mono">{h.previous}</TableCell>
                    <TableCell className="font-mono font-semibold">
                      {h.new_value}
                      {h.override && <Badge variant="outline" className="ml-2 bg-violet-50 text-violet-700 border-violet-200">OVERRIDE</Badge>}
                    </TableCell>
                    <TableCell className="text-[12.5px]">{h.reason}</TableCell>
                    <TableCell className="text-[12.5px]">{h.changed_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------- Helpers -------
function StatTile({ label, value, hint, icon: Icon, tone = "default", loading }) {
  const tones = {
    default: "bg-white border-border",
    primary: "bg-[#234935] text-white border-transparent",
    success: "bg-emerald-50 border-emerald-100",
    warning: "bg-amber-50 border-amber-100",
    danger:  "bg-red-50 border-red-100",
    violet:  "bg-violet-50 border-violet-100",
  };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  const iconBg = tone === "primary" ? "bg-white/10 text-[#C5A059]"
    : tone === "success" ? "bg-emerald-100 text-emerald-700"
    : tone === "warning" ? "bg-amber-100 text-amber-700"
    : tone === "danger"  ? "bg-red-100 text-red-700"
    : tone === "violet"  ? "bg-violet-100 text-violet-700"
    : "bg-[#F6EED9] text-[#8A6B2B]";
  return (
    <div className={`rounded-lg border ${tones[tone]} p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
          {loading ? <Skeleton className="h-7 w-14 mt-2" /> :
            <div className={`mt-1.5 font-display text-[26px] font-semibold leading-none ${valueColor}`}>{value ?? 0}</div>}
          {hint && <div className={`mt-1.5 text-[11px] ${tone === "primary" ? "text-white/60" : "text-muted-foreground"}`}>{hint}</div>}
        </div>
        <div className={`rounded-md p-1.5 ${iconBg}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, items, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[170px] bg-white" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((it) => <SelectItem key={it.v} value={it.v}>{it.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function F({ label, value, set, testId, placeholder }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
      <Input value={value || ""} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}
